import Papa from 'papaparse';

// Interactive runs are capped so a 39k-row test cannot race the 60s ALB idle
// timeout in front of menu-service. Unlimited is reserved for a deliberate
// download.
export const DEFAULT_TEST_LIMIT = 5000;
export const MAX_TEST_LIMIT = 100000;

export const FORMAT_OPTIONS = [
  {
    value: 'final',
    label: 'Final stage columns',
    help: 'Whatever the last step actually selects. Use this while authoring — it shows the real output.',
  },
  {
    value: 'canonical',
    label: 'Canonical columns',
    help: 'The seven columns the pipeline consumes, normalized. Use this to confirm a chain is ingestible.',
  },
];

/**
 * Turn the store's steps into the `steps[]` payload the endpoint expects.
 *
 * Only order/name/sql are sent: enabled state is deliberately ignored, because
 * the whole point of a test is to see what a chain does before it is enabled.
 */
export const buildStepsPayload = (steps) =>
  steps.map((step, index) => ({
    order: index + 1,
    name: (step.name || '').trim(),
    sql: (step.sql || '').trim(),
  }));

/** Seed the editable-chain scratchpad from a store's saved steps. */
export const draftFromSteps = (steps) =>
  steps.map((step) => ({
    key: `saved-${step.id}`,
    name: step.name || '',
    sql: step.sql || '',
  }));

/**
 * Parse the CSV response into headers + rows for table display.
 *
 * Papa is already a dependency here and handles quoted fields and embedded
 * newlines, which a naive split on ',' would mangle.
 */
export const parseTransformCsv = (csv) => {
  if (!csv || !csv.trim()) return { headers: [], rows: [] };

  const result = Papa.parse(csv.trim(), {
    skipEmptyLines: true,
  });

  const [headers = [], ...rows] = result.data || [];
  return { headers, rows };
};

/** Trigger a browser download of the raw CSV the endpoint returned. */
export const downloadCsv = (csv, filename) => {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename || 'transform_test.csv';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Validate an S3 key the way the endpoint does, so an obvious typo fails here
 * rather than after a round trip.
 *
 * Keys are relative to the saucey-menu-ftp bucket: `{store}/{file}` for a plain
 * store, `{chain}/{store}/{file}` for a chain.
 */
export const validateS3Key = (raw, storeId) => {
  const key = (raw || '').trim();
  if (!key) return 'An S3 key is required.';
  if (key.startsWith('/')) return 'Drop the leading slash — the key is relative to the bucket.';
  if (key.includes('..')) return 'The key cannot contain "..".';
  if (key.startsWith('s3://') || key.includes('saucey-menu-ftp/')) {
    return 'Give the key only, without the bucket name.';
  }
  if (!key.includes('/')) {
    return `Expected a path like ${storeId}/filename.csv — the key includes the store folder.`;
  }
  return null;
};

export const validateLimit = (raw) => {
  if (raw === '' || raw === null || raw === undefined) return null;
  const n = Number(raw);
  if (!Number.isInteger(n) || n < 0) return 'Limit must be a whole number, or 0 for all rows.';
  if (n > MAX_TEST_LIMIT) return `Limit cannot exceed ${MAX_TEST_LIMIT.toLocaleString()}.`;
  return null;
};

/**
 * Describe the per-step row funnel from the X-CDC-Step-Rows header.
 *
 * Returns null when the header did not reach the browser, which happens unless
 * menu-service lists it in Access-Control-Expose-Headers — a custom response
 * header is not readable from JS by default on a cross-origin response.
 */
export const buildFunnel = (sourceRows, stepRows, steps) => {
  if (!stepRows || stepRows.length === 0) return null;

  const stages = [{ label: 'source', rows: sourceRows, dropped: null }];
  let previous = sourceRows;

  stepRows.forEach((rows, i) => {
    stages.push({
      label: steps[i]?.name || `step ${i + 1}`,
      rows,
      dropped: typeof previous === 'number' ? previous - rows : null,
    });
    previous = rows;
  });

  return stages;
};
